import { NgClass } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';

export const MINUTES_IN_HOUR = 60;
export const HOURS_IN_DAY = 24;
export const MAX_TIME_LENGTH = 5;

@Component({
  selector: 'app-time-input',
  standalone: true,
  imports: [FormsModule, TranslateModule, NgClass],
  templateUrl: './time-input.component.html',
  styleUrl: './time-input.component.scss'
})
export class TimeInputComponent implements OnInit, OnChanges {
  @Input() time: string = '';
  @Input() label: string = '';
  @Input() invalid: boolean = false;
  @Input() disabled: boolean = false;
  @Input() required: boolean = true;
  @Output() timeChange: EventEmitter<string> = new EventEmitter<string>();
  @Output() blured: EventEmitter<void> = new EventEmitter<void>();
  value: string = '';

  ngOnInit() {
    this.value = this.disabled || !this.time ? '' : this.convertTimeToString(this.time);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['time'] && changes['time'].currentValue !== changes['time'].previousValue) {
      this.value = this.disabled || !this.time ? '' : this.convertTimeToString(this.time);
    }
  }

  convertTimeToString(value: string): string {
    const [hours = '', minutes = ''] = value.split(':');
    return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
  }

  convertToHHmmSSFormat(value: string): string {
    const [hours, minutes] = value.split(':').map((part) => part.padStart(2, '0'));
    return `${hours}:${minutes}:00`;
  }

  isValidTimeString(value: string): boolean {
    if (value.split(':').length !== 2 || value.length !== MAX_TIME_LENGTH) {
      return false;
    }

    const [hours, minutes] = value.split(':').map(Number);

    return hours >= 0 && hours <= HOURS_IN_DAY - 1 && minutes >= 0 && minutes <= MINUTES_IN_HOUR - 1;
  }

  allowOnlySpecifiedCharacters(event: KeyboardEvent): void {
    const allowedCharacters = '0123456789:';
    const isSelected =
      event.target && (event.target as HTMLInputElement).selectionStart !== (event.target as HTMLInputElement).selectionEnd;

    if (
      !allowedCharacters.includes(event.key) ||
      (this.value.includes(':') && event.key === ':') ||
      (this.value.length >= MAX_TIME_LENGTH &&
        !['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab'].includes(event.key) &&
        !isSelected) ||
      !this.isCorrectTime(this.value, event.key)
    ) {
      event.preventDefault();
    }
  }

  onBlur(): void {
    this.blured.emit();
  }

  onAnyChange(): void {
    if (this.value.length === 2 && !this.value.includes(':')) {
      this.value += ':';
    }

    this.value = this.value.replace(/[^\d:]/g, '');
    if (this.value.length > MAX_TIME_LENGTH) {
      this.value = this.value.slice(0, MAX_TIME_LENGTH);
    }

    if (this.isValidTimeString(this.value)) {
      this.timeChange.emit(this.convertToHHmmSSFormat(this.value));
    } else {
      this.timeChange.emit('');
    }
  }

  isCorrectTime(value: string, key: string): boolean {
    if (value.length === 0) {
      return /[0-2]/.test(key);
    }

    if (value.length === 1) {
      const firstDigit = Number(value[0]);

      if (firstDigit === 2) {
        return /[0-4]/.test(key);
      } else {
        return /[0-9]/.test(key);
      }
    }

    if (value.length === 3) {
      const hours = Number(value.replace(':', ''));

      if (hours === HOURS_IN_DAY) {
        return key === '0';
      } else {
        return /[0-5]/.test(key);
      }
    }

    if (value.length === 4) {
      const hours = Number(value.replace(':', ''));

      if (hours === HOURS_IN_DAY) {
        return key === '0';
      } else {
        return /[0-9]/.test(key);
      }
    }

    return this.isValidTimeString(value);
  }
}
